import { useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion, useInView } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Zap, BarChart3, Clock, ArrowRight, Shield, Target, Settings, Lock, Brain, Globe, DollarSign, Plug, Workflow} from 'lucide-react';
import { fadeInUp, staggerContainer, scaleIn } from '@/lib/animations';
import HeroCanvas from '@/components/three/HeroCanvas';
import { HowItWorksDesktop } from '@/components/info/HowItWorks';
import { InfoTooltip, TooltipBullet } from '@/components/info/Util';
import { TooltipProvider } from '@/components/ui/tooltip';
import { CALLS_HANDLED, HOURS_SAVED } from '@/lib/constants';

function AnimatedCounter({ target, suffix = '', label }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  return (
    <div ref={ref} className="text-center" data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <motion.div
        initial={{ opacity: 0, scale: 0.5 }}
        animate={isInView ? { opacity: 1, scale: 1 } : {}}
        transition={{ duration: 0.5, ease: 'backOut' }}
        className="text-4xl md:text-5xl font-extrabold text-white mb-2"
      >
        {isInView ? target : '0'}{suffix}
      </motion.div>
      <p className="text-sm text-blue-100/80">{label}</p>
    </div>
  );
}

function HeroSection() {
  const words1 = "AI & Automation".split(' ');
  const words2 = "that thinks the way you do.".split(' ');
  const highlighted = ['AI', 'Automation'];

  return (
    <section data-testid="hero-section" className="relative min-h-screen flex items-center overflow-hidden">
      <HeroCanvas />
      <div className="grain-overlay" />
      <div className="hero hero-split">
        <div className="hero-grid">
          <motion.div variants={staggerContainer} initial="hidden" animate="visible" className="lg:col-span-7">
            <div className="heading">
              {words1.map((word, i) => (
                <motion.span key={i} custom={i} variants={{ hidden: { opacity: 0, y: 30 }, visible: (idx) => ({ opacity: 1, y: 0, transition: { delay: 0.2 + idx * 0.1, duration: 0.5, ease: [0.22, 1, 0.36, 1] } }) }} initial="hidden" animate="visible"
                  className={`text-5xl sm:text-6xl lg:text-7xl font-extrabold tracking-tight ${highlighted.includes(word) ? 'logo-gradient-text' : 'text-brand-white'}`}
                >{word}</motion.span>
              ))}
            </div>

            <div className="heading">
              {words2.map((word, i) => (
                <motion.span key={i} custom={i + words1.length} variants={{ hidden: { opacity: 0, y: 30 }, visible: (idx) => ({ opacity: 1, y: 0, transition: { delay: 0.2 + idx * 0.1, duration: 0.5, ease: [0.22, 1, 0.36, 1] } }) }} initial="hidden" animate="visible"
                  className="text-5xl sm:text-6xl lg:text-7xl font-extrabold tracking-tight text-brand-white"
                >{word}</motion.span>
              ))}
            </div>

            {/* Tagline bullets */}
            <motion.div variants={fadeInUp} className="space-y-2 mb-3">
              <p className="text-base md:text-lg text-brand-white flex items-start gap-2">
                <span className="text-brand-blue mt-0.5">&#8594;</span>
                Custom AI. Grown from your business.
              </p>
              <p className="text-base md:text-lg text-brand-white flex items-start gap-2">
                <span className="text-brand-blue mt-0.5">&#8594;</span>
                Not generic AI. <span className="text-brand-white font-semibold">Your AI.</span>
              </p>
              <p className="text-base md:text-lg text-brand-white flex items-start gap-2">
                <span className="text-brand-blue mt-0.5">&#8594;</span>
                Automation built in. <span className="text-brand-white font-semibold">Not just AI.</span>
              </p>
            </motion.div>

            <motion.div variants={fadeInUp} className="flex flex-wrap gap-4 mb-10">
              <Link to="/contact/" data-testid="hero-cta-primary" className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-brand-blue text-white font-semibold transition-all duration-300 shadow-[0_0_20px_rgba(var(--c-purple-rgb),0.3)] hover:shadow-[0_0_40px_rgba(var(--c-purple-rgb),0.6)] hover:bg-brand-blue-light">
                Get Started <ArrowRight size={18} />
              </Link>
              <Link to="/services/" data-testid="hero-cta-secondary" className="inline-flex items-center gap-2 px-8 py-4 rounded-full font-semibold transition-all duration-300 text-brand-white backdrop-blur-sm" style={{ border: '1px solid var(--brand-border)' }}>
                See How It Works
              </Link>
            </motion.div>

          </motion.div>
          <div className="hidden lg:block lg:col-span-5" />
        </div>
      </div>
    </section>
  );
}

function AutomationSection() {
  const capabilities = [
    {
      icon: Plug,
      title: 'Connect to What You Already Use',
      desc: "We plug your AI directly into the systems you already run your business on — CRM, calendars, inboxes, and comms tools — so it works with what you have, not around it.",
    },
    {
      icon: Workflow,
      title: 'Build What You’re Missing',
      desc: "No CRM, workflow, or notification system in place yet? We'll build it. New CRMs, integrations, automated workflows, and alerts — set up and connected to your AI from day one.",
    },
  ];

  return (
    <section data-testid="automation-section" className="py-24 md:py-32 bg-brand-black">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-100px' }} variants={staggerContainer} className="text-center mb-16">
          <motion.p variants={fadeInUp} className="subtitle">AUTOMATION</motion.p>
          <motion.h2 variants={fadeInUp} className="title">
            AI Is Only Half the Story. <span className="text-brand-blue">Automation Is the Rest.</span>
          </motion.h2>
          <motion.p variants={fadeInUp} className="text-base text-brand-muted max-w-2xl mx-auto leading-relaxed">
            For us, automation means bringing automation and systems into your existing business — not just adding an AI on top. It boosts your team's productivity and connects everything to the AI we build for you.
          </motion.p>
        </motion.div>

        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={staggerContainer} className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          {capabilities.map((cap) => (
            <motion.div key={cap.title} variants={fadeInUp} data-testid={`automation-card-${cap.title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`} className="glass-card glass-card-hover p-8">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-5" style={{ background: 'rgba(var(--c-purple-rgb),0.1)' }}>
                <cap.icon size={24} className="text-brand-blue" />
              </div>
              <h3 className="text-xl font-bold text-brand-white mb-3">{cap.title}</h3>
              <p className="text-sm text-brand-muted leading-relaxed">{cap.desc}</p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

function BenefitsSection() {
  const benefits = [
    { icon: Brain, title: 'Created & Trained For You', desc: 'Every AI agent is custom-built and trained on your business data, FAQs, and workflows.' },
    { icon: BarChart3, title: 'Custom Dashboard', desc: 'Monitor calls, chats, and performance metrics from a single, intuitive dashboard.' },
    { icon: Settings, title: 'CRM Integration', desc: 'Works with Zoho, Agile, HubSpot, Salesforce, and more — seamless data sync.' },
    { icon: Clock, title: 'Save Time', desc: 'Automate repetitive calls and chats so your team focuses on high-value work.' },
    { icon: DollarSign, title: 'Drive Operational Efficiency', desc: 'Lower costs while maintaining a high-quality customer experience.' },
    { icon: Target, title: 'Accuracy', desc: 'AI trained on your exact data delivers consistent, precise responses every time.' },
    { icon: Lock, title: 'Privacy First', desc: 'Your data stays yours. We never use client data to train models.' },
    { icon: Zap, title: 'Easy Setup', desc: 'No technical team needed. We handle tech, systems, and ongoing support.' },
  ];

  return (
    <section data-testid="benefits-section" className="py-24 md:py-32 bg-brand-black">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-100px' }} variants={staggerContainer} className="text-center mb-16">
          <motion.p variants={fadeInUp} className="subtitle">WHY CHOOSE US</motion.p>
          <motion.h2 variants={fadeInUp} className="title">
            Built for <span className="text-brand-blue">Results</span>
          </motion.h2>
          <motion.p variants={fadeInUp} className="text-base text-brand-muted max-w-lg mx-auto">
            Deliver great customer experiences more efficiently — optimise costs without compromising quality.
          </motion.p>
        </motion.div>

        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={staggerContainer} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {benefits.map((b) => (
            <motion.div key={b.title} variants={fadeInUp} className="glass-card glass-card-hover p-6 flex flex-col gap-3">
              <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(var(--c-purple-rgb),0.1)' }}>
                <b.icon size={20} className="text-brand-blue" />
              </div>
              <div>
                <h4 className="text-sm font-bold text-brand-white mb-1.5 flex items-center gap-1.5">
                  {b.title}
                </h4>
                <p className="text-xs text-brand-muted leading-relaxed">{b.desc}</p>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

function SecuritySection() {
  const certifications = [
    {
      icon: Shield,
      label: 'ISO\n27001',
      title: 'ISO/IEC 27001:2022',
      desc: 'The gold standard for information security management. Your data is protected to the highest organisational standards.'
    },
    {
      icon: Lock,
      label: 'Encrypted',
      title: 'End-to-End Encryption',
      desc: 'All data encrypted at rest and in transit. We never use your client data to train models — ever.'
    },
  ];

  return (
    <section data-testid="security-section" className="py-24 md:py-32 bg-brand-charcoal relative overflow-hidden">
      <div className="absolute inset-0 opacity-[0.03]" style={{
        backgroundImage: `radial-gradient(circle at 1px 1px, currentColor 1px, transparent 0)`,
        backgroundSize: '40px 40px'
      }} />

      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 relative z-10">
        {/* Header — Voiceflow-style left-aligned */}
        <motion.div
          initial="hidden" whileInView="visible" viewport={{ once: true }}
          variants={staggerContainer}
          className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16"
        >
          <div>
            <motion.p variants={fadeInUp} className="subtitle">
              Security & Compliance
            </motion.p>
            <motion.h2 variants={fadeInUp} className="title">
              ISO Certified &amp;<br />Enterprise Ready
            </motion.h2>
          </div>
          <motion.p variants={fadeInUp} className="text-sm text-brand-muted max-w-sm leading-relaxed md:text-right">
            Built to meet the requirements of security-conscious businesses. Your data stays private, encrypted, and fully under your control.
          </motion.p>
        </motion.div>

        {/* Divider */}
        <div className="w-full h-px mb-16" style={{ background: 'var(--brand-border)' }} />

        {/* Certification Cards */}
        <motion.div
          initial="hidden" whileInView="visible" viewport={{ once: true }}
          variants={staggerContainer}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8"
        >
          {certifications.map((cert) => (
            <motion.div
              key={cert.title}
              variants={fadeInUp}
              data-testid={`cert-card-${cert.title.toLowerCase().replace(/[\s\/:.]+/g, '-')}`}
              className="flex flex-col"
            >
              {/* Badge with icon + label text */}
              <div
                className="w-20 h-20 rounded-full flex flex-col items-center justify-center gap-0.5 mb-8 mx-auto md:mx-0"
                style={{ background: 'var(--brand-glass)', border: '1px solid var(--brand-glass-border)' }}
              >
                <cert.icon size={18} className="text-brand-blue" />
                <span className="text-[8px] font-bold text-brand-off-white text-center leading-tight px-2 whitespace-pre-line">{cert.label}</span>
              </div>

              <h4 className="text-base font-bold text-brand-white mb-2">{cert.title}</h4>
              <p className="text-xs text-brand-muted leading-relaxed">{cert.desc}</p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

function StatsSection() {
  return (
    <section data-testid="stats-section" className="py-20 bg-gradient-to-r from-brand-blue-dark via-brand-blue to-brand-blue-dark relative overflow-hidden">
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(circle at 20% 50%, rgba(255,255,255,0.2) 0%, transparent 50%), radial-gradient(circle at 80% 50%, rgba(255,255,255,0.1) 0%, transparent 50%)' }} />
      </div>
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 relative z-10">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-12">
          <AnimatedCounter target={CALLS_HANDLED.toString()} suffix="+" label="Calls Handled" />
          <AnimatedCounter target={HOURS_SAVED.toString()} suffix="+" label="Hours Saved" />
          <AnimatedCounter target="24/7" label="Coverage" />
          <AnimatedCounter target="100" suffix="%" label="Custom-Built" />
        </div>
      </div>
    </section>
  );
}

function CTASection() {
  return (
    <section data-testid="cta-section" className="py-24 md:py-32 bg-brand-charcoal relative">
      <div className="max-w-3xl mx-auto px-6 md:px-12 lg:px-16 text-center">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-100px' }} variants={staggerContainer}>
          <motion.h2 variants={fadeInUp} className="text-4xl sm:text-5xl font-bold text-brand-white tracking-tight mb-6">
            Ready to Deploy AI in <span className="text-brand-blue">Your Business?</span>
          </motion.h2>
          <motion.p variants={fadeInUp} className="text-base text-brand-muted mb-10 max-w-lg mx-auto">
            Start with a free discovery call. We'll assess your needs and build a custom AI solution tailored to your business.
          </motion.p>
          <motion.div variants={scaleIn}>
            <Link to="/contact/" data-testid="cta-book-call" className="inline-flex items-center gap-2 px-10 py-5 rounded-full bg-brand-blue text-white font-semibold text-lg transition-all duration-300 shadow-[0_0_30px_rgba(var(--c-purple-rgb),0.4)] hover:shadow-[0_0_50px_rgba(var(--c-purple-rgb),0.7)] hover:bg-brand-blue-light animate-pulse-glow">
              Book Your Free Call <ArrowRight size={20} />
            </Link>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}

export default function HomePage() {
  return (
    <TooltipProvider delayDuration={150}>
      <div data-testid="home-page">
        <Helmet>
          <title>Custum AI - AI Tailored To Your Business</title>
          <meta name="description" content="AI Callers, Receptionists, and Webchat agents that are custom built for Australian businesses." />
          <meta property="og:title" content="Custum AI - AI Tailored To Your Business" />
          <meta property="og:description" content="AI Callers, Receptionists, and Webchat agents created and trained for your business." />
          <meta property="og:type" content="website" />
          <link rel="canonical" href="https://custum.ai/" />
        </Helmet>
        <HeroSection />
        <HowItWorksDesktop />
        <BenefitsSection />
        {/* <SecuritySection /> */}
        <StatsSection />
        <CTASection />
      </div>
    </TooltipProvider>
  );
}
